import * as React from "react";
import { useCallback, useContext, useMemo, useRef, useState } from "react";
import styled, { css } from "styled-components";
import { createSelector } from "@reduxjs/toolkit";
import ReactGridContainer from "components/common/ReactGridContainer";
import { widgetDefinition } from "views/logic/Widgets";
import WidgetPosition from "views/logic/widgets/WidgetPosition";
import WidgetFocusContext from "views/components/contexts/WidgetFocusContext";
import InteractiveContext from "views/components/contexts/InteractiveContext";
import ElementDimensions from "components/common/ElementDimensions";
import useViewsSelector from "views/stores/useViewsSelector";
import { selectViewStates, selectIsDirty } from "views/logic/slices/viewSelectors";
import findGaps from "views/components/GridGaps";
import generateId from "logic/generateId";
import NewWidgetPlaceholder from "views/components/NewWidgetPlaceholder";
import CreateNewWidgetModal from "views/components/CreateNewWidgetModal";
import isDeepEqual from "stores/isDeepEqual";
import useViewsDispatch from "views/stores/useViewsDispatch";
import { updateWidgetPositions, updateWidgetPosition } from "views/logic/slices/widgetActions";
import { setIsDirty } from "views/logic/slices/viewSlice";
import { widgetDragHandleClass } from "views/components/widgets/Constants";
import { selectNewWidget } from "views/logic/slices/widgetsSlice";
import ScrollToHint from "views/components/common/ScrollToHint";
import WidgetContainer from "./WidgetContainer";
import WidgetComponent from "./WidgetComponent";
import useWidgets from "../hooks/useWidgets";
const COLUMNS = {
  xxl: 12,
  xl: 12,
  lg: 12,
  md: 12,
  sm: 12,
  xs: 12
};
const DashboardWrap = styled(ElementDimensions)(
  ({ theme }) => css`
    color: ${theme.colors.text.primary};
    margin: 0;
    width: 100%;
    height: 100%;
  `
);
const StyledReactGridContainer = styled(ReactGridContainer)(
  ({ $hasFocusedWidget }) => css`
    height: ${$hasFocusedWidget ? "100% !important" : "100%"};
    max-height: ${$hasFocusedWidget ? "100%" : "auto"};
    overflow: ${$hasFocusedWidget ? "hidden" : "visible"};
    transition: none;
  `
);
const _defaultDimensions = (type) => {
  const widgetDef = widgetDefinition(type);
  return new WidgetPosition(1, 1, widgetDef.defaultHeight, widgetDef.defaultWidth);
};
const WidgetGridItem = ({ onPositionsChange, positions, widgetId, focusedWidget, isNewWidget }) => {
  const editing = (focusedWidget == null ? void 0 : focusedWidget.id) === widgetId && (focusedWidget == null ? void 0 : focusedWidget.editing);
  const widgetPosition = positions[widgetId];
  return /* @__PURE__ */ React.createElement(React.Fragment, null, /* @__PURE__ */ React.createElement(
    ScrollToHint,
    {
      autoScroll: true,
      scrollContainer: { current: document.body },
      title: "Scroll to new widget",
      ifTrue: isNewWidget
    }
  ), /* @__PURE__ */ React.createElement(
    WidgetComponent,
    {
      editing,
      onPositionsChange,
      position: widgetPosition,
      widgetId
    }
  ));
};
const generatePositions = (widgets, positions) => Object.fromEntries(
  widgets.toArray().map(({ id, type }) => {
    var _a;
    return [id, (_a = positions[id]) != null ? _a : _defaultDimensions(type)];
  })
);
const selectWidgetPositions = createSelector(
  selectViewStates,
  (viewStates) => Object.fromEntries(viewStates.toArray().flatMap(({ widgetPositions }) => Object.entries(widgetPositions)))
);
const useWidgetsAndPositions = () => {
  const initialPositions = useViewsSelector(selectWidgetPositions);
  const widgets = useWidgets();
  const positions = useMemo(() => generatePositions(widgets, initialPositions), [widgets, initialPositions]);
  return [widgets, positions];
};
const Grid = ({ children, locked, onPositionsChange, onSyncLayout = void 0, positions, width }) => {
  const { focusedWidget } = useContext(WidgetFocusContext);
  return /* @__PURE__ */ React.createElement(
    StyledReactGridContainer,
    {
      $hasFocusedWidget: !!(focusedWidget == null ? void 0 : focusedWidget.id),
      columns: COLUMNS,
      isResizable: !(focusedWidget == null ? void 0 : focusedWidget.id),
      locked,
      positions,
      measureBeforeMount: true,
      onPositionsChange,
      onSyncLayout,
      width,
      draggableHandle: `.${widgetDragHandleClass}`
    },
    children
  );
};
const MAXIMUM_GRID_SIZE = 12;
const convertPosition = ({ col, row, height, width }) => new WidgetPosition(col, row, height, width >= MAXIMUM_GRID_SIZE ? Infinity : width);
const onPositionChange = (dispatch, newPosition) => {
  const { id } = newPosition;
  const widgetPosition = convertPosition(newPosition);
  return dispatch(updateWidgetPosition(id, widgetPosition));
};
const _onPositionsChange = (dispatch, newPositions, setLastUpdate) => {
  const widgetPositions = Object.fromEntries(
    newPositions.map((newPosition) => [newPosition.id, convertPosition(newPosition)])
  );
  setLastUpdate(generateId());
  return dispatch(updateWidgetPositions(widgetPositions));
};
const _onSyncLayout = (positions, newPositions) => (dispatch, getState) => {
  const isDirty = selectIsDirty(getState());
  const widgetPositions = Object.fromEntries(
    newPositions.map((newPosition) => [newPosition.id, convertPosition(newPosition)])
  );
  if (!isDeepEqual(positions, widgetPositions)) {
    dispatch(updateWidgetPositions(widgetPositions)).then(() => dispatch(setIsDirty(isDirty)));
  }
};
const renderGaps = (widgets, positions) => {
  const gaps = findGaps(widgets.map((widget) => positions[widget.id]));
  const _positions = { ...positions };
  const gapsItems = gaps.map((gap) => {
    const id = `gap-${generateId()}`;
    const { col, row, height, width } = gap;
    const gapPosition = WidgetPosition.builder().col(col).row(row).height(height).width(width).build();
    _positions[id] = gapPosition;
    return /* @__PURE__ */ React.createElement(NewWidgetPlaceholder, { key: id, position: gapPosition, component: CreateNewWidgetModal });
  });
  return [gapsItems, _positions];
};
const WidgetGrid = () => {
  const isInteractive = useContext(InteractiveContext);
  const { focusedWidget } = useContext(WidgetFocusContext);
  const [lastUpdate, setLastUpdate] = useState(void 0);
  const preventDoubleUpdate = useRef();
  const dispatch = useViewsDispatch();
  const newWidget = useViewsSelector(selectNewWidget);
  const [widgets, positions] = useWidgetsAndPositions();
  const onPositionsChange = useCallback(
    (newPositions2) => {
      preventDoubleUpdate.current = newPositions2;
      return _onPositionsChange(dispatch, newPositions2, setLastUpdate);
    },
    [dispatch]
  );
  const _onPositionChange = useCallback(
    (newPosition) => onPositionChange(dispatch, newPosition),
    [dispatch]
  );
  const onSyncLayout = useCallback(
    (newPositions2) => {
      if (!isDeepEqual(preventDoubleUpdate.current, newPositions2)) {
        dispatch(_onSyncLayout(positions, newPositions2));
      }
    },
    [dispatch, positions]
  );
  const [children, newPositions] = useMemo(() => {
    const widgetItems = widgets.toArray().filter((widget) => !!positions[widget.id]).map(({ id: widgetId }) => /* @__PURE__ */ React.createElement(
      WidgetContainer,
      {
        key: widgetId,
        className: "widgetFrame",
        "data-widget-id": widgetId,
        isFocused: (focusedWidget == null ? void 0 : focusedWidget.id) === widgetId && (focusedWidget == null ? void 0 : focusedWidget.focusing)
      },
      /* @__PURE__ */ React.createElement(
        WidgetGridItem,
        {
          isNewWidget: newWidget === widgetId,
          positions,
          widgetId,
          focusedWidget,
          onPositionsChange: _onPositionChange
        }
      )
    ));
    if (isInteractive) {
      const [gapItems, _positions] = renderGaps(widgets.toArray(), positions);
      return [[...widgetItems, ...gapItems], _positions];
    }
    return [widgetItems, positions];
  }, [focusedWidget, isInteractive, lastUpdate, positions, widgets]);
  return /* @__PURE__ */ React.createElement(DashboardWrap, null, ({ width }) => /* @__PURE__ */ React.createElement(
    Grid,
    {
      locked: !isInteractive,
      positions: newPositions,
      onPositionsChange,
      onSyncLayout,
      width
    },
    children
  ));
};
WidgetGrid.displayName = "WidgetGrid";
export default WidgetGrid;
